import React, { useCallback, useRef } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { ListNode, ListItemNode } from "@lexical/list";
import { LinkNode, AutoLinkNode } from "@lexical/link";
import { $generateHtmlFromNodes } from "@lexical/html";
import { VariableNode } from "./VariableNode";
import { ImageNode } from "./ImageNode";
import ToolbarPlugin from "./ToolbarPlugin";
import FloatingLinkEditor from "./FloatingLinkEditor";
import ImagePlugin from "./ImagePlugin";
import ContentInitPlugin from "./ContentInitPlugin";

const EDITOR_THEME = {
  paragraph: "contract-lexical-paragraph",
  text: {
    bold: "contract-lexical-bold",
    italic: "contract-lexical-italic",
    underline: "contract-lexical-underline",
  },
  list: {
    ol: "contract-lexical-ol",
    ul: "contract-lexical-ul",
    listitem: "contract-lexical-li",
  },
  link: "contract-lexical-link",
};

const LexicalEditor = ({ initialContent, onChange, onUploadError }) => {
  const initialConfig = {
    namespace: "ContractTemplateEditor",
    theme: EDITOR_THEME,
    nodes: [
      ListNode,
      ListItemNode,
      LinkNode,
      AutoLinkNode,
      VariableNode,
      ImageNode,
    ],
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error("Lexical error:", error);
    },
  };

  const isFirstChange = useRef(true);

  const handleChange = useCallback(
    (editorState, editor) => {
      // Skip the initial change triggered by ContentInitPlugin
      if (isFirstChange.current) {
        isFirstChange.current = false;
        return;
      }
      editorState.read(() => {
        const html = $generateHtmlFromNodes(editor);
        onChange?.(html);
      });
    },
    [onChange]
  );

  return (
    <div className="contract-lexical-container">
      <LexicalComposer initialConfig={initialConfig}>
        <ToolbarPlugin onError={onUploadError} />
        <div className="contract-lexical-inner">
          <RichTextPlugin
            contentEditable={
              <ContentEditable className="contract-lexical-content" />
            }
            placeholder={
              <div className="contract-lexical-placeholder">
                Start writing your contract…
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
        </div>
        <HistoryPlugin />
        <ListPlugin />
        <LinkPlugin />
        <ImagePlugin />
        <FloatingLinkEditor />
        <ContentInitPlugin initialContent={initialContent} />
        <OnChangePlugin onChange={handleChange} ignoreSelectionChange />
      </LexicalComposer>
    </div>
  );
};

export default LexicalEditor;
